const HOME_BOUNDS_TOLERANCE = 0.000001
const FULLSCREEN_RESIZE_SETTLE_FRAMES = 1

function getAnimationFrameScheduler() {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return callback => window.requestAnimationFrame(callback)
  }

  return callback => setTimeout(callback, 0)
}

function getAnimationFrameCanceller() {
  if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    return frameId => window.cancelAnimationFrame(frameId)
  }

  return frameId => clearTimeout(frameId)
}

function getResizeSettleFrames(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : FULLSCREEN_RESIZE_SETTLE_FRAMES
}

function getFiniteBoundsValue(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function areBoundsClose(bounds, expectedBounds, tolerance = HOME_BOUNDS_TOLERANCE) {
  if (!bounds || !expectedBounds) {
    return false
  }

  return ['x', 'y', 'width', 'height'].every(key => {
    const boundsValue = getFiniteBoundsValue(bounds[key])
    const expectedBoundsValue = getFiniteBoundsValue(expectedBounds[key])

    if (boundsValue === null || expectedBoundsValue === null) {
      return false
    }

    return Math.abs(boundsValue - expectedBoundsValue) <= tolerance
  })
}

export function isViewerAtHome(viewer, tolerance = HOME_BOUNDS_TOLERANCE) {
  const viewport = viewer && viewer.viewport

  if (
    !viewport ||
    typeof viewport.getBounds !== 'function' ||
    typeof viewport.getHomeBounds !== 'function'
  ) {
    return false
  }

  return areBoundsClose(viewport.getBounds(true), viewport.getHomeBounds(), tolerance)
}

export function shouldResetHomeOnFullscreen(viewer, hasUserAdjustedViewport, hasInitialViewport = false) {
  if (hasUserAdjustedViewport || hasInitialViewport) {
    return isViewerAtHome(viewer)
  }

  return true
}

export function resetViewerHomeImmediately(viewer) {
  const viewport = viewer && viewer.viewport

  if (!viewport) {
    return
  }

  if (typeof viewport.goHome === 'function') {
    viewport.goHome(true)
  }

  if (typeof viewport.applyConstraints === 'function') {
    viewport.applyConstraints(true)
  }

  if (typeof viewer.forceRedraw === 'function') {
    viewer.forceRedraw()
  }
}

export function prepareViewerHomeResetOnNextResize(viewer, options = {}) {
  if (!viewer || !viewer.viewport) {
    return null
  }

  let isActive = true
  let hasReset = false
  let hasForceResizeStarted = false
  let pendingFrame = null
  const scheduleFrame =
    typeof options.scheduleFrame === 'function' ? options.scheduleFrame : getAnimationFrameScheduler()
  const cancelFrame =
    typeof options.cancelFrame === 'function' ? options.cancelFrame : getAnimationFrameCanceller()
  const resizeSettleFrames = getResizeSettleFrames(options.resizeSettleFrames)

  const clearPendingFrame = () => {
    if (pendingFrame === null) {
      return
    }

    cancelFrame(pendingFrame)
    pendingFrame = null
  }

  const resetHome = () => {
    if (!isActive || hasReset) {
      return
    }

    hasReset = true
    clearPendingFrame()
    resetViewerHomeImmediately(viewer)
  }

  const scheduleAfterFrames = (remainingFrames, callback) => {
    if (!isActive || hasReset) {
      return
    }

    if (remainingFrames <= 0) {
      callback()
      return
    }

    pendingFrame = scheduleFrame(() => {
      pendingFrame = null
      scheduleAfterFrames(remainingFrames - 1, callback)
    })
  }

  const forceResizeThenResetHome = () => {
    if (!isActive || hasReset) {
      return
    }

    if (typeof viewer.forceResize === 'function') {
      viewer.forceResize()
    }

    if (typeof viewer.forceRedraw === 'function') {
      viewer.forceRedraw()
    }

    pendingFrame = scheduleFrame(() => {
      pendingFrame = null
      resetHome()
    })
  }

  return {
    cancel: () => {
      isActive = false
      clearPendingFrame()
    },
    forceResize: () => {
      if (!isActive || hasReset || hasForceResizeStarted) {
        return
      }

      hasForceResizeStarted = true
      scheduleAfterFrames(resizeSettleFrames, forceResizeThenResetHome)
    }
  }
}
